import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Info, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getAccessToken } from '@/components/AuthProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * E-Mail Dialog zum Versenden von Rechnungen
 * Verwendet MSAL Token für /me/sendMail Endpoint
 */
const EmailDialog = ({
  isOpen,
  onClose,
  ergebnis,
  rechnungsNr,
  variant = "ba",
  onGeneratePdf
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailData, setEmailData] = useState({
    subject: "",
    body: "",
    recipientEmail: "",
    recipientName: "",
    ccEmail: "",
  });

  useEffect(() => {
    if (isOpen) {
      loadTemplatesAndRecipients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadTemplatesAndRecipients = async () => {
    try {
      const [templatesRes, recipientsRes] = await Promise.all([
        axios.get(`${API}/email/templates`),
        axios.get(`${API}/email/recipients`)
      ]);
      setTemplates(templatesRes.data);
      setRecipients(recipientsRes.data);

      // Auto-select template based on variant
      const matchingTemplate = templatesRes.data.find(t =>
        (variant === "ba" && t.recipientType === "ba") ||
        (variant === "privat" && t.recipientType === "privat")
      );
      if (matchingTemplate) {
        applyTemplate(matchingTemplate);
      }

      // Auto-select recipient
      const matchingRecipient = recipientsRes.data.find(r => r.type === variant);
      if (matchingRecipient) {
        setEmailData(prev => ({
          ...prev,
          recipientEmail: matchingRecipient.email,
          recipientName: matchingRecipient.name
        }));
      }
    } catch (error) {
      console.error("Fehler beim Laden der E-Mail-Daten:", error);
    }
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    const zusammenfassung = ergebnis?.zusammenfassung || {};
    const data = variant === "ba" ? ergebnis?.baRechnung : ergebnis?.privatRechnung;
    const formatCurrency = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

    // Template-Variablen ersetzen
    let subject = template.subject
      .replace('{rechnungsNr}', rechnungsNr)
      .replace('{klient}', zusammenfassung.klient || '');

    let body = template.body
      .replace('{klient}', zusammenfassung.klient || '')
      .replace('{aktenzeichen}', ergebnis?.bewilligungInfo?.aktenzeichen || '-')
      .replace('{rechnungsNr}', rechnungsNr)
      .replace('{zeitraum}', zusammenfassung.abrechnungszeitraum || '-')
      .replace('{betrag}', formatCurrency(data?.rechnungsbetrag));

    setEmailData(prev => ({ ...prev, subject, body }));
  };

  const handleSend = async () => {
    if (!emailData.recipientEmail || !emailData.subject) {
      toast.error("Bitte Empfänger und Betreff ausfüllen");
      return;
    }

    setIsLoading(true);
    try {
      // MSAL Access Token holen für /me Endpoint
      const accessToken = await getAccessToken();

      // PDF generieren
      let pdfBase64 = null;
      let pdfFilename = null;

      if (onGeneratePdf) {
        const pdfResult = await onGeneratePdf(variant);
        if (pdfResult) {
          pdfBase64 = pdfResult.base64;
          pdfFilename = pdfResult.filename;
        }
      }

      // E-Mail senden (mit accessToken wird /me/sendMail verwendet)
      const response = await axios.post(`${API}/email/send`, {
        subject: emailData.subject,
        recipients: [
          { email: emailData.recipientEmail, name: emailData.recipientName, type: "to" },
          ...(emailData.ccEmail ? [{ email: emailData.ccEmail, type: "cc" }] : [])
        ],
        body: emailData.body,
        pdfBase64,
        pdfFilename,
        accessToken, // MSAL Token für /me Endpoint - E-Mail wird als angemeldeter User gesendet
        rechnungId: ergebnis?.zusammenfassung?.rechnungsNr
      });

      if (response.data.success) {
        if (response.data.status === "sent") {
          toast.success("E-Mail wurde erfolgreich gesendet!");
        } else if (response.data.status === "queued") {
          toast.info("E-Mail wurde in die Warteschlange eingereiht. Versand nach Entra ID Konfiguration.");
        }
        onClose();
      } else {
        toast.error(response.data.error || "Fehler beim Senden");
      }
    } catch (error) {
      toast.error("Fehler beim Senden der E-Mail");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {variant === "ba" ? "BA-Rechnung per E-Mail senden" : "Privatrechnung per E-Mail senden"}
          </DialogTitle>
          <DialogDescription>
            Rechnung {rechnungsNr} an Empfänger senden
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Template-Auswahl */}
          <div>
            <Label className="text-sm">Vorlage</Label>
            <Select
              value={selectedTemplate?.id || ""}
              onValueChange={(id) => {
                const t = templates.find(t => t.id === id);
                if (t) applyTemplate(t);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Vorlage auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Empfänger */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Empfänger E-Mail *</Label>
              <Input
                value={emailData.recipientEmail}
                onChange={(e) => setEmailData({...emailData, recipientEmail: e.target.value})}
                placeholder="empfaenger@beispiel.de"
                data-testid="email-recipient"
              />
            </div>
            <div>
              <Label className="text-sm">Empfänger Name</Label>
              <Input
                value={emailData.recipientName}
                onChange={(e) => setEmailData({...emailData, recipientName: e.target.value})}
                placeholder="Name des Empfängers"
              />
            </div>
          </div>

          {/* CC */}
          <div>
            <Label className="text-sm">CC (optional)</Label>
            <Input
              value={emailData.ccEmail}
              onChange={(e) => setEmailData({...emailData, ccEmail: e.target.value})}
              placeholder="cc@beispiel.de"
            />
          </div>

          {/* Betreff */}
          <div>
            <Label className="text-sm">Betreff *</Label>
            <Input
              value={emailData.subject}
              onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
              placeholder="Betreff der E-Mail"
              data-testid="email-subject"
            />
          </div>

          {/* Nachricht */}
          <div>
            <Label className="text-sm">Nachricht</Label>
            <Textarea
              value={emailData.body}
              onChange={(e) => setEmailData({...emailData, body: e.target.value})}
              placeholder="Nachrichtentext..."
              rows={8}
              className="font-sans text-sm"
              data-testid="email-body"
            />
          </div>

          {/* Info */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-700 text-sm">
              Die Rechnung wird als PDF-Anhang ({rechnungsNr}.pdf) mitgesendet.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Abbrechen
          </Button>
          <Button onClick={handleSend} disabled={isLoading} data-testid="email-send-btn">
            {isLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sende...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Senden</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Memo für Performance - Dialog muss nicht re-rendern wenn Parent sich ändert
export default React.memo(EmailDialog);
